import { initContract } from '@ts-rest/core';
import { z } from 'zod';
const c = initContract();
export const AuthTokensSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number(),
    tokenType: z.string(),
});
export const MfaRequiredSchema = z.object({
    mfaRequired: z.literal(true),
    challengeId: z.string(),
    /** Masked (j***@domain) — display only. */
    email: z.string(),
});
export const LoginResponseSchema = z.union([
    MfaRequiredSchema,
    AuthTokensSchema,
]);
export function isMfaRequired(response) {
    return 'mfaRequired' in response;
}
export const LoginBodySchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
export const VerifyMfaBodySchema = z.object({
    challengeId: z.string().min(1),
    code: z.string().length(6),
});
export const RegisterWithInvitationBodySchema = z.object({
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    email: z.string().email(),
    password: z.string().min(1),
    invitationId: z.string().uuid(),
});
export const CurrentUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
});
/**
 * Authentication flows. The pre-auth routes are public: the client attaches
 * no meaningful token, the backend ignores any.
 */
export const authContract = c.router({
    login: {
        method: 'POST',
        path: '/auth/login',
        body: LoginBodySchema,
        responses: { 200: LoginResponseSchema },
        summary: 'Password login — returns tokens, or an MFA challenge',
    },
    verifyMfa: {
        method: 'POST',
        path: '/auth/mfa/verify',
        body: VerifyMfaBodySchema,
        responses: { 200: AuthTokensSchema },
        summary: 'Verify the emailed OTP and obtain the tokens',
    },
    resendMfa: {
        method: 'POST',
        path: '/auth/mfa/resend',
        body: z.object({ challengeId: z.string().min(1) }),
        responses: {
            200: z.object({ success: z.boolean(), challengeId: z.string() }),
        },
        summary: 'Resend the OTP (rotates the challenge)',
    },
    refreshToken: {
        method: 'POST',
        path: '/auth/refresh',
        body: z.object({ refreshToken: z.string().min(1) }),
        responses: { 200: AuthTokensSchema },
        summary: 'Exchange a refresh token for fresh tokens',
    },
    registerWithInvitation: {
        method: 'POST',
        path: '/auth/register-with-invitation',
        body: RegisterWithInvitationBodySchema,
        responses: { 201: AuthTokensSchema },
        summary: 'Create the account of an invited user and log in',
    },
    getMe: {
        method: 'GET',
        path: '/auth/me',
        responses: { 200: CurrentUserSchema },
        summary: 'Identity of the authenticated user',
    },
    requestPasswordReset: {
        method: 'POST',
        path: '/auth/password-reset/request',
        body: z.object({ email: z.string().email() }),
        responses: { 201: z.object({ success: z.boolean() }) },
        summary: 'Email a password-reset link (always succeeds)',
    },
    resetPassword: {
        method: 'POST',
        path: '/auth/password-reset/reset',
        body: z.object({
            token: z.string().min(1),
            newPassword: z.string().min(1),
        }),
        responses: { 201: z.object({ success: z.boolean() }) },
        summary: 'Set a new password from a reset token',
    },
    validateResetToken: {
        method: 'POST',
        path: '/auth/password-reset/validate-token',
        body: z.object({ token: z.string().min(1) }),
        responses: { 201: z.object({ valid: z.boolean() }) },
        summary: 'Check a reset token before showing the form',
    },
    requestMyPasswordReset: {
        method: 'POST',
        path: '/users/me/request-password-reset',
        body: c.noBody(),
        responses: { 204: c.noBody() },
        summary: 'Email a reset link to the authenticated user',
    },
});
/** Platform-admin user management (AdminAuthGuard server-side). */
export const adminUsersContract = c.router({
    disableUser: {
        method: 'POST',
        path: '/users/:id/disable',
        pathParams: z.object({ id: z.string().min(1) }),
        body: z.undefined(),
        responses: { 204: z.undefined() },
        summary: 'Disable a user account (admin)',
    },
    enableUser: {
        method: 'POST',
        path: '/users/:id/enable',
        pathParams: z.object({ id: z.string().min(1) }),
        body: z.undefined(),
        responses: { 204: z.undefined() },
        summary: 'Re-enable a user account (admin)',
    },
});
